/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef, useState } from 'react';

import { jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { RovoThinkingIndicator } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingIndicator';
import { RovoGradientKeyframesStyle } from '@atlassian/rovo-platform-ui-components/rovo-gradient';

import type { RemixOptionKind } from '../../../index.ts';

const messages = defineMessages({
	escHint: {
		id: 'malleable-ui-components.remix-thinking-bar.esc-hint',
		defaultMessage: 'Esc',
		description: 'Keyboard hint showing the Esc key can be used to cancel',
	},
	stopLabel: {
		id: 'malleable-ui-components.remix-thinking-bar.stop-label',
		defaultMessage: 'Stop remixing',
		description: 'Accessible label for the stop button',
	},
	rovoIsThinking: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a remix',
	},
	analyzingContent: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content for a remix',
	},
	puttingPiecesTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content for a remix',
	},
	structuringRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.structuring-remix',
		defaultMessage: 'Structuring your remix',
		description: 'Loading message shown while Rovo is structuring the remix output',
	},
	designingRemix: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.designing-remix',
		defaultMessage: 'Designing your remix',
		description: 'Loading message shown while Rovo is designing the remix output',
	},
	addingItAllTogether: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements of the remix',
	},
	applyingFinalTouches: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing the remix',
	},
	almostDone: {
		id: 'malleable-ui-components.remix-thinking-bar.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished generating a remix',
	},
});

/**
 * Interval in ms between each rotating thinking message step.
 */
const ROTATION_INTERVAL_MS = 4000;

/**
 * Hook that cycles through an ordered list of strings at a fixed interval,
 * advancing sequentially (not shuffled). Stays on the last item once exhausted.
 *
 * The `texts` array is treated as stable on mount — only the initial value is
 * used to set up the interval, so callers should ensure the array does not
 * change identity unnecessarily (e.g. by defining it outside the render or
 * memoising it).
 */
function useSequentialText(texts: string[]): string {
	const [index, setIndex] = useState(0);
	const indexRef = useRef(0);
	// Keep latest texts accessible inside the interval callback without
	// re-creating the interval on every render.
	const textsRef = useRef(texts);
	textsRef.current = texts;

	useEffect(() => {
		if (textsRef.current.length <= 1) {
			return;
		}

		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			if (next < textsRef.current.length) {
				indexRef.current = next;
				setIndex(next);
			} else {
				// Sequence exhausted — stay on the last message
				clearInterval(interval);
			}
		}, ROTATION_INTERVAL_MS);

		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Mount-only: the sequence is fixed for the lifetime of the component

	return textsRef.current[index] ?? textsRef.current[0] ?? '';
}


const styles = cssMap({
	gradientWrapper: {
		position: 'fixed',
		bottom: token('space.200'),
		left: token('space.050'),
		right: token('space.050'),
		width: 'fit-content',
		margin: '0 auto',
		zIndex: 600,
		animation: 'rovoGradientRotation linear 2s infinite',
		borderRadius: token('radius.xxlarge'),
	},
	gradientInner: {
		paddingBlock: token('space.025'),
		paddingInline: token('space.025'),
		backgroundColor: token('elevation.surface.overlay'),
		borderRadius: 'inherit',
		backgroundClip: 'content-box',
		boxShadow: token('elevation.shadow.overlay'),
	},
	fixedWrapper: {
		paddingInline: token('space.200'),
		paddingBlock: token('space.100'),
		borderRadius: token('radius.xxlarge'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: token('space.100'),
		minWidth: '400px',
	},
	escBadge: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},
});

type RemixThinkingBarProps = {
	onStop: () => void;
	optionType?: RemixOptionKind;
};

/**
 * A floating thinking bar that displays a Rovo loading animation, a sequentially
 * rotating status text, an Esc keyboard hint, and a stop button. Matches the
 * visual style of the inline chat ThinkingView / AI loading screen.
 *
 * The status text progresses through a fixed ordered sequence:
 * "Rovo is thinking" → "Analyzing your content" → "Putting the pieces together"
 * → "Structuring your remix" → "Designing your remix" → "Adding it all together"
 * → "Applying the final touches" → "Almost done"
 */
export function RemixThinkingBar({ onStop, optionType: _optionType }: RemixThinkingBarProps) {
	const { formatMessage } = useIntl();

	const thinkingTexts = [
		formatMessage(messages.rovoIsThinking),
		formatMessage(messages.analyzingContent),
		formatMessage(messages.puttingPiecesTogether),
		formatMessage(messages.structuringRemix),
		formatMessage(messages.designingRemix),
		formatMessage(messages.addingItAllTogether),
		formatMessage(messages.applyingFinalTouches),
		formatMessage(messages.almostDone),
	];

	const thinkingTitle = useSequentialText(thinkingTexts);

	return (
		<div
			css={styles.gradientWrapper}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- gradient background not expressible in cssMap
			style={{ background: 'var(--rovo-gradient-background)' }}
			data-testid="remix-thinking-bar-gradient-border"
		>
			<RovoGradientKeyframesStyle />
			<div css={styles.gradientInner}>
				<Box as="div" xcss={styles.fixedWrapper} testId="remix-thinking-bar">
					<RovoThinkingIndicator title={thinkingTitle} />
					<Inline alignBlock="center" space="space.100">
						<Box as="span" xcss={styles.escBadge}>
							{formatMessage(messages.escHint)}
						</Box>
						<IconButton
							shape="circle"
							onClick={onStop}
							icon={(iconProps) => (
								<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label="" />
							)}
							label={formatMessage(messages.stopLabel)}
						/>
					</Inline>
				</Box>
			</div>
		</div>
	);
}
